/* =========================================================================
   LID LAB — type motion
   Progressive enhancement only. Real text always ships in the DOM, so crawlers
   and no-JS visitors get the finished copy; this just animates the reveal.
   Everything is disabled under prefers-reduced-motion.

   Behaviours
   ----------
   [data-ll-type]        typewriter. With data-ll-type-wrong it mistypes first,
                         backspaces to the divergence point and corrects itself.
   [data-ll-scramble]    character scramble that resolves left to right on reveal.
   [data-ll-roll]        digit roll for instrument readouts.
   auto                  section labels and indices scramble; readout figures roll.
   ambient               occasional short screen glitch.
   ========================================================================= */
(function () {
  'use strict';

  /* ll-head.liquid is rendered by every ll-* section, so this file can be
     requested several times on one page. Without this guard each copy starts
     its own observers and animation loops, and concurrent loops fight over the
     same text nodes so nothing ever settles. Initialise once only. */
  if (window.__llMotionInit) return;
  window.__llMotionInit = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  var GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\|<>[]{}+=*#%';
  var rand = function (n) { return Math.floor(Math.random() * n); };
  var glyph = function () { return GLYPHS.charAt(rand(GLYPHS.length)); };

  /* Lock the rendered height before we empty an element, so nothing reflows. */
  function lock(el) {
    var h = el.getBoundingClientRect().height;
    if (h) el.style.minHeight = h + 'px';
  }
  function unlock(el) {
    setTimeout(function () { el.style.minHeight = ''; }, 400);
  }

  /* ---------------------------------------------------------- typewriter */
  /* Driven off requestAnimationFrame rather than chained setTimeout. Chained
     timers get clamped to ~1s per tick in a backgrounded tab, which turns a
     2-second reveal into a 30-second crawl that is still going when the visitor
     comes back to it. One clock for all the motion in here. */
  function typeOut(el) {
    var final = (el.getAttribute('data-ll-final') || el.textContent).replace(/\s+/g, ' ').trim();
    var wrong = el.getAttribute('data-ll-type-wrong');
    var speed = parseInt(el.getAttribute('data-ll-speed'), 10) || 34;

    el.setAttribute('aria-label', final);
    lock(el);
    el.textContent = '';
    el.classList.add('ll-caret');

    // build [text, duration] pairs, then play them against elapsed time
    var steps = [];
    function keys(str, from, to, ms) {
      if (to >= from) { for (var i = from; i <= to; i++) steps.push([str.slice(0, i), ms]); }
      else { for (var j = from; j >= to; j--) steps.push([str.slice(0, j), ms]); }
    }
    if (wrong) {
      wrong = wrong.replace(/\s+/g, ' ').trim();
      var d = 0;
      while (d < wrong.length && d < final.length && wrong[d] === final[d]) d++;
      keys(wrong, 1, wrong.length, speed);          // mistype it
      steps.push([wrong, 520]);                      // let it sit there
      keys(wrong, wrong.length - 1, d, speed * 0.6); // backspace to the divergence
      steps.push([wrong.slice(0, d), 170]);          // beat before the correction
      keys(final, d + 1, final.length, speed);       // type the real thing
    } else {
      keys(final, 1, final.length, speed);
    }

    var total = 0, i;
    for (i = 0; i < steps.length; i++) { total += steps[i][1]; steps[i].push(total); }

    var t0 = null, at = 0;
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var e = ts - t0;
      while (at < steps.length && steps[at][2] <= e) at++;
      if (at >= steps.length) {
        el.textContent = final;
        el.classList.remove('ll-caret');
        unlock(el);
        return;
      }
      if (el.textContent !== steps[at][0]) el.textContent = steps[at][0];
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------ scramble */
  /* Duration-bounded on purpose. A frame-counted loop drifts on a slow frame
     and, if anything ever double-starts it, can churn indefinitely. Deriving
     progress from elapsed time means it always lands. */
  function scramble(el, opts) {
    opts = opts || {};
    var node = opts.node || firstTextNode(el);
    if (!node) return;
    var final = node.nodeValue;
    var chars = final.split('');
    var dur = opts.duration || 360;
    var t0 = null;

    el.classList.add('ll-scrambling');
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var settled = Math.floor(p * chars.length);
      var out = '';
      for (var i = 0; i < chars.length; i++) {
        if (i < settled || /\s/.test(chars[i])) out += chars[i];
        else out += glyph();
      }
      node.nodeValue = out;
      if (p < 1) {
        requestAnimationFrame(frame);
      } else {
        node.nodeValue = final;
        el.classList.remove('ll-scrambling');
      }
    }
    requestAnimationFrame(frame);
  }

  function firstTextNode(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3 && el.childNodes[i].nodeValue.trim()) return el.childNodes[i];
    }
    return null;
  }

  /* ----------------------------------------------------------- digit roll */
  function roll(el) {
    var node = firstTextNode(el);
    if (!node) return;
    var final = node.nodeValue.trim();
    if (!/^[\d.,]+$/.test(final)) { scramble(el); return; }
    var target = parseFloat(final.replace(/,/g, ''));
    var dur = 620, t0 = null;
    var pad = final.length;

    el.classList.add('ll-scrambling');
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      // ease out, with a couple of overshoot flickers early on
      var eased = 1 - Math.pow(1 - p, 3);
      var val = target * eased;
      if (p < 0.55) val = target * eased + rand(Math.max(1, Math.round(target * 0.35)));
      node.nodeValue = String(Math.round(val)).slice(0, Math.max(pad, 1));
      if (p < 1) requestAnimationFrame(step);
      else { node.nodeValue = final; el.classList.remove('ll-scrambling'); }
    }
    requestAnimationFrame(step);
  }

  /* -------------------------------------------------- reveal orchestration */
  /* Claim an element exactly once. The global guard above stops a second copy
     of this file from initialising, but a soft navigation or a section re-render
     can still call init() again — and two loops writing the same text node makes
     it churn forever instead of settling. */
  function claim(el) {
    if (el.getAttribute('data-ll-claimed')) return false;
    el.setAttribute('data-ll-claimed', '1');
    return true;
  }

  function onReveal(el, fn, delay) {
    if (!claim(el)) return;
    if (!('IntersectionObserver' in window)) { fn(el); return; }
    var seen = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting || seen) return;
        seen = true;
        io.disconnect();
        setTimeout(function () { fn(el); }, delay || 0);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.2 });
    io.observe(el);
  }

  function init() {
    // explicit opt-ins
    document.querySelectorAll('[data-ll-type]').forEach(function (el) {
      onReveal(el, typeOut, parseInt(el.getAttribute('data-ll-delay'), 10) || 260);
    });
    document.querySelectorAll('[data-ll-scramble]').forEach(function (el, i) {
      onReveal(el, function (e) { scramble(e); }, 60 * (i % 4));
    });
    document.querySelectorAll('[data-ll-roll]').forEach(function (el, i) {
      onReveal(el, roll, 90 * (i % 3));
    });

    // automatic: the technical furniture reads as instrumentation, so it churns
    document.querySelectorAll('.ll-sec-idx, .ll-sec-title, .ll-nav-prod-name').forEach(function (el, i) {
      if (el.hasAttribute('data-ll-scramble')) return;
      onReveal(el, function (e) { scramble(e, { per: 1 }); }, 40 * (i % 5));
    });
    document.querySelectorAll('.ll-ro strong').forEach(function (el, i) {
      onReveal(el, roll, 100 * (i % 3));
    });
    document.querySelectorAll('.ll-proto dd, .ll-hero-strip i').forEach(function (el, i) {
      onReveal(el, function (e) { scramble(e, { per: 1 }); }, 30 * (i % 6));
    });

    ambient();
  }

  /* ------------------------------------------------------- screen glitch */
  function ambient() {
    var layer = document.createElement('div');
    layer.className = 'll-glitch-layer';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);

    function fire() {
      if (document.hidden) return schedule();
      document.documentElement.classList.add('ll-glitching');
      layer.classList.add('is-on');
      setTimeout(function () {
        document.documentElement.classList.remove('ll-glitching');
        layer.classList.remove('is-on');
      }, 90 + rand(110));
      schedule();
    }
    function schedule() {
      setTimeout(fire, 22000 + rand(26000));
    }
    schedule();
  }

  /* Start promptly. Waiting on document.fonts.ready alone means a cold Google
     Fonts cache can hold every animation back for several seconds, by which
     time the visitor has already scrolled past the hero. Give fonts a short
     head start so glyph widths are settled, then go regardless. */
  function boot() {
    var started = false;
    function go() { if (!started) { started = true; init(); } }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go).catch(go);
    }
    setTimeout(go, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
